import React, { useLayoutEffect, useRef } from "react";
import {
  PhoneOff,
  UserCog,
  History,
  PhoneCall,
  CalendarCheck,
  Users2,
  Smartphone,
  Calendar,
  Database,
  Mic,
  PhoneIncoming,
  ClipboardCheck,
  Brain,
  AudioLines,
  Heart,
  Building2,
  Stethoscope,
  Sparkles,
  MapPin
} from "lucide-react";
import Navbar from "../../Components/Navbar";
import ChatbotWidget from "../ChatComponent";
import TrustStatement from "../../Components/TrustStatement";

import Footer from "../../Components/Footer";
import "../../Styling/Landing_Pages_css/Voicebot.css";
import HeroLandingPage from "../../Components/LandingPagesComponents/Hero_Landing_Page";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" },
};

const staggerContainer = {
  initial: { opacity: 0 },
  whileInView: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
  viewport: { once: true },
};

export default function VoicebotLandingPage() {
  const canvasRef = useRef(null);

  useLayoutEffect(() => {
    // Beam Logic (adapted from Truck Dispatch)
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width, height, beams = [];

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", resize);
    resize();

    class Beam {
      constructor() {
        this.reset();
      }
      reset() {
        this.y = Math.random() * height;
        this.x = -500;
        this.speed = 2 + Math.random() * 5;
        this.width = 200 + Math.random() * 300;
        this.opacity = 0.1 + Math.random() * 0.3;
      }
      update() {
        this.x += this.speed;
        if (this.x > width) this.reset();
      }
      draw() {
        const gradient = ctx.createLinearGradient(this.x, 0, this.x + this.width, 0);
        gradient.addColorStop(0, "transparent");
        gradient.addColorStop(0.5, `rgba(124, 58, 237, ${this.opacity})`); // Voicebot Primary Purple
        gradient.addColorStop(1, "transparent");
        ctx.fillStyle = gradient;
        ctx.fillRect(this.x, this.y, this.width, 4);
      }
    }

    for (let i = 0; i < 12; i++) beams.push(new Beam());

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      beams.forEach(b => {
        b.update();
        b.draw();
      });
      requestAnimationFrame(animate);
    };

    animate();

    const sections = gsap.utils.toArray('section');
    sections.forEach(section => {
      gsap.fromTo(section,
        { opacity: 0, scale: 0.95 },
        {
          opacity: 1,
          scale: 1,
          duration: 1.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: section,
            start: "top 85%"
          }
        }
      );
    });

    return () => window.removeEventListener("resize", resize);
  }, []);

  const slides = [
    {
      image: "/images/Landing_page_images/AI_voice/ai_voice_1.png",
      heading: "Every Missed Call Is a Missed Patient",
      heading2: "Turn Inbound Calls Into Confirmed Appointments",
      subheading: "Automatically answer inbound patient calls, manage appointments, and reduce front-desk overload for hospitals, medical centers, and clinics."
    },
    {
      image: "/images/Landing_page_images/AI_voice/ai_voice_2.png",
      heading: "Don’t Let Leads Slip Through the Cracks",
      heading2: "Automated Follow-Ups That Close Deals",
      subheading:
        "Our AI doesn’t just answer calls—it nurtures leads. It sends instant text follow-ups and keeps potential customers engaged until they are on your calendar.",
    },
    {
      image: "/images/Landing_page_images/AI_voice/ai_voice_3.png",
      heading: "Focus on the Work, Not the Phone",
      heading2: "Your 24/7 AI Front Desk",
      subheading:
        "Stop interrupting your day to answer basic questions. Our AI handles FAQs, scheduling, and intake, so you and your team can stay focused on high-value tasks.",
    },
  ];

  return (
    <>
      <Navbar></Navbar>

      {/* GSAP Animated Background */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: -1,
          pointerEvents: 'none',
          background: '#cbd5e1' // Silverish background to match body
        }}
      />
      <HeroLandingPage
        slides={slides}
        badgeText="AI VOICE ASSISTANT FOR MEDICAL & HEALTHCARE PRACTICES"
        primaryBtn={{
          text: "Request Medical Demo →",
          link: "https://studio.digitaldreamworksagency.com/access",
        }}
      />

      <section className="voicebot-problem-section">
        <div className="container">
          <motion.h2
            className="voicebot-problem-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Missed Calls Lead to Delayed Care & Lost Revenue
          </motion.h2>
          <motion.p
            className="voicebot-problem-desc"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Studies show 30% of calls to local businesses go unanswered. That's
            30% of your potential revenue walking straight to your competitors.
          </motion.p>

          <motion.div
            className="voicebot-problem-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div className="voicebot-problem-card" variants={fadeInUp}>
              <div className="voicebot-problem-icon-wrapper">
                <PhoneOff className="voicebot-problem-icon-svg" />
              </div>
              <p className="voicebot-problem-text">
                High inbound call volume during peak clinic hours
              </p>
            </motion.div>
            <motion.div className="voicebot-problem-card" variants={fadeInUp}>
              <div className="voicebot-problem-icon-wrapper">
                <UserCog className="voicebot-problem-icon-svg" />
              </div>
              <p className="voicebot-problem-text">
                Front desk overloaded with repetitive scheduling tasks
              </p>
            </motion.div>
            <motion.div className="voicebot-problem-card" variants={fadeInUp}>
              <div className="voicebot-problem-icon-wrapper">
                <History className="voicebot-problem-icon-svg" />
              </div>
              <p className="voicebot-problem-text">
                Missed patient calls reduce satisfaction and retention
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="voicebot-process-section">
        <div className="container">
          <motion.h2
            className="voicebot-process-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            From Incoming Call to Confirmed Appointment
          </motion.h2>
          <motion.p
            className="voicebot-process-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            See how the AI assistant handles every call automatically
          </motion.p>

          <motion.div
            className="voicebot-process-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div className="voicebot-process-card" variants={fadeInUp}>
              <div className="voicebot-process-icon">
                <PhoneIncoming size={24} />
              </div>
              <h3 className="voicebot-process-title-card">Patient Calls Your Practice</h3>
              <p className="voicebot-process-desc">
                AI assistant answers within 2 rings
              </p>
            </motion.div>
            <motion.div className="voicebot-process-card" variants={fadeInUp}>
              <div className="voicebot-process-icon">
                <Mic size={24} />
              </div>
              <h3 className="voicebot-process-title-card">
                Identifies Patient Intent (Booking, Reschedule, Info)
              </h3>
              <p className="voicebot-process-desc">
                Identifies booking, inquiry, or consultation request
              </p>
            </motion.div>
            <motion.div className="voicebot-process-card" variants={fadeInUp}>
              <div className="voicebot-process-icon">
                <CalendarCheck size={24} />
              </div>
              <h3 className="voicebot-process-title-card">Books Appointment or Executes Request</h3>
              <p className="voicebot-process-desc">
                Books appointment or captures lead info
              </p>
            </motion.div>
            <motion.div className="voicebot-process-card" variants={fadeInUp}>
              <div className="voicebot-process-icon">
                <Smartphone size={24} />
              </div>
              <h3 className="voicebot-process-title-card">
                Confirms Appointment and Logs Interaction              </h3>
              <p className="voicebot-process-desc">
                Sends confirmation and reminder texts
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="voicebot-handles-section">
        <div className="container">
          <motion.h2
            className="voicebot-handles-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What Your AI Assistant Handles
          </motion.h2>

          <motion.div
            className="voicebot-handles-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div className="voicebot-handles-card" variants={fadeInUp}>
              <div className="voicebot-handles-icon">
                <PhoneCall size={24} />
              </div>
              <h3 className="voicebot-handles-card-title">Inbound Patient Calls</h3>
              <p className="voicebot-handles-desc">
                Answers calls instantly, including peak hours and after-hours.
              </p>
            </motion.div>
            <motion.div className="voicebot-handles-card" variants={fadeInUp}>
              <div className="voicebot-handles-icon">
                <CalendarCheck size={24} />
              </div>
              <h3 className="voicebot-handles-card-title">
                Appointment Booking & Scheduling
              </h3>
              <p className="voicebot-handles-desc">
                New bookings, reschedules, and cancellations handled automatically.
              </p>
            </motion.div>
            <motion.div className="voicebot-handles-card" variants={fadeInUp}>
              <div className="voicebot-handles-icon">
                <Users2 size={24} />
              </div>
              <h3 className="voicebot-handles-card-title">Patient Intake & Information</h3>
              <p className="voicebot-handles-desc">
                Collects basic patient details and answers common questions.
              </p>
            </motion.div>
            <motion.div className="voicebot-handles-card" variants={fadeInUp}>
              <div className="voicebot-handles-icon">
                <ClipboardCheck size={24} />
              </div>
              <h3 className="voicebot-handles-card-title">
                Routing & Escalation
              </h3>
              <p className="voicebot-handles-desc">
                Transfers urgent or complex cases to staff when required.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="voicebot-integrations-section">
        <div className="container">
          <motion.h2
            className="voicebot-integrations-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Integrates Into Your Operations
          </motion.h2>
          <motion.p
            className="voicebot-integrations-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Works with your existing clinic or hospital phone systems and scheduling tools
          </motion.p>

          <motion.div
            className="voicebot-integrations-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div
              className="voicebot-integration-card"
              variants={fadeInUp}
            >
              <div className="voicebot-integration-icon">
                <PhoneCall size={32} color="#7c3aed" />
              </div>
              <h3 className="voicebot-integration-title">
                Connects to your phone line
              </h3>
              <p className="voicebot-integration-desc">
                Integrates with your existing clinic phone numbers
              </p>
            </motion.div>
            <motion.div
              className="voicebot-integration-card"
              variants={fadeInUp}
            >
              <div className="voicebot-integration-icon">
                <Calendar size={32} color="#7c3aed" />
              </div>
              <h3 className="voicebot-integration-title">
                Syncs with your calendar
              </h3>
              <p className="voicebot-integration-desc">
                Syncs with medical scheduling calendars (Google, Outlook, internal systems)
              </p>
            </motion.div>
            <motion.div
              className="voicebot-integration-card"
              variants={fadeInUp}
            >
              <div className="voicebot-integration-icon">
                <Database size={32} color="#7c3aed" />
              </div>
              <h3 className="voicebot-integration-title">
                Updates your CRM seamlessly
              </h3>
              <p className="voicebot-integration-desc">
                Logs calls and actions into patient or CRM systems
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="voicebot-notchatbot-section">
        <div className="container">
          <motion.div
            className="voicebot-notchatbot-wrapper"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="voicebot-notchatbot-header">
              <h2 className="voicebot-notchatbot-title">This Is Not a Chatbot</h2>
              <p className="voicebot-notchatbot-desc">
                Unlike text-based bots that frustrate customers, our AI understands natural conversation,
                handles complex nuances, and delivers a human-level experience.
                <br /><strong>It's built for voice — not for typing.</strong>
              </p>
            </div>

            <motion.div
              className="voicebot-notchatbot-grid"
              variants={staggerContainer}
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true }}
            >
              <motion.div
                className="voicebot-feature-card"
                variants={fadeInUp}
              >
                <div className="voicebot-feature-icon-box purple">
                  <AudioLines size={32} />
                </div>
                <div className="voicebot-feature-content">
                  <h3 className="voicebot-feature-title">
                    Not Robotic
                  </h3>
                  <p className="voicebot-feature-desc">
                    Sounds like a real person on your team. Uses natural pauses, intonation, and empathy.
                  </p>
                </div>
              </motion.div>

              <motion.div
                className="voicebot-feature-card"
                variants={fadeInUp}
              >
                <div className="voicebot-feature-icon-box pink">
                  <Heart size={32} />
                </div>
                <div className="voicebot-feature-content">
                  <h3 className="voicebot-feature-title">
                    Customer Focused
                  </h3>
                  <p className="voicebot-feature-desc">
                    Prioritizes satisfaction. It doesn't just route calls; it solves problems and builds trust.
                  </p>
                </div>
              </motion.div>

              <motion.div
                className="voicebot-feature-card"
                variants={fadeInUp}
              >
                <div className="voicebot-feature-icon-box blue">
                  <Brain size={32} />
                </div>
                <div className="voicebot-feature-content">
                  <h3 className="voicebot-feature-title">
                    Learns Your Business
                  </h3>
                  <p className="voicebot-feature-desc">
                    Customized for your workflows. It knows your services, pricing, and scheduling rules inside out.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="voicebot-perfect-section">
        <div className="container">
          <motion.h2
            className="voicebot-perfect-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Perfect For
          </motion.h2>

          <motion.div
            className="voicebot-perfect-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div className="voicebot-perfect-card" variants={fadeInUp}>
              <div className="voicebot-perfect-icon">
                <Building2 size={32} />
              </div>
              <h3 className="voicebot-perfect-card-title">
                Hospitals & Medical Centers
              </h3>
              <p className="voicebot-perfect-card-desc">
                High inbound call volumes across departments.
              </p>
            </motion.div>
            <motion.div className="voicebot-perfect-card" variants={fadeInUp}>
              <div className="voicebot-perfect-icon">
                <Stethoscope size={32} />
              </div>
              <h3 className="voicebot-perfect-card-title">
                Dental Clinics
              </h3>
              <p className="voicebot-perfect-card-desc">
                Appointment-heavy scheduling and patient inquiries.
              </p>
            </motion.div>
            <motion.div className="voicebot-perfect-card" variants={fadeInUp}>
              <div className="voicebot-perfect-icon">
                <Sparkles size={32} />
              </div>
              <h3 className="voicebot-perfect-card-title">Dermatology & Specialty Clinics</h3>
              <p className="voicebot-perfect-card-desc">
                Consultation bookings and follow-ups.
              </p>
            </motion.div>
            <motion.div className="voicebot-perfect-card" variants={fadeInUp}>
              <div className="voicebot-perfect-icon">
                <MapPin size={32} />
              </div>
              <h3 className="voicebot-perfect-card-title">Multi-Location Practices</h3>
              <p className="voicebot-perfect-card-desc">
                Centralized call handling across locations.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <div className="LD-divider"></div>
      <ChatbotWidget></ChatbotWidget>

      <div className="AT-cta-section">
        <h3 className="AT-cta-title">Ready to See It Handle Real Patient Calls?</h3>
        <p className="AT-cta-desc" style={{ color: 'rgba(255,255,255,0.85)', maxWidth: '750px', margin: '1rem auto 2.5rem', lineHeight: '1.6', fontSize: '1.15rem' }}>
          Live demo using real medical workflows. No generic presentations.
        </p>
        <div className="AT-cta-buttons">
          <a
            href="https://studio.digitaldreamworksagency.com/access"
            className="AT-btn-white"
            style={{ textDecoration: "none" }}
            onClick={() => {
              if (window.fbq) {
                window.fbq('trackCustom', 'RequestDemoOpened', {
                  source: 'voicebot_medical_demo'
                });
              }
            }}
          >
            Request Medical Demo
          </a>

          <a
            href="https://calendly.com/digi-dreamworks/onboarding-call"
            className="AT-btn-outlined"
            style={{ textDecoration: "none" }}
          >
            Book Operational Call
          </a>
        </div>

      </div>
      <TrustStatement></TrustStatement>
      <Footer></Footer>
    </>
  );
}

